import { useCallback, useRef } from 'react';

type UsePopupWindowOptions = {
  fallback?: (url: string) => void | Promise<void>;
};

type UsePopupWindowReturn = {
  openPopup: () => void;
  navigatePopup: (url: string) => Promise<void>;
  closePopup: () => void;
};

/**
 * Hook for opening popup windows that works on Safari/iOS.
 * Opens the window synchronously during user gesture to avoid pop-up blockers.
 */
export function usePopupWindow(
  options?: UsePopupWindowOptions,
): UsePopupWindowReturn {
  const popupWindowRef = useRef<Window | null>(null);

  const openPopup = useCallback(() => {
    popupWindowRef.current = window.open('about:blank', '_blank');
  }, []);

  const navigatePopup = useCallback(
    async (url: string) => {
      try {
        const popupWindow = popupWindowRef.current;
        if (popupWindow && !popupWindow.closed) {
          popupWindow.location.href = url;
        } else if (options?.fallback) {
          await options.fallback(url);
        }
      } finally {
        popupWindowRef.current = null;
      }
    },
    [options],
  );

  const closePopup = useCallback(() => {
    const popupWindow = popupWindowRef.current;
    if (popupWindow && !popupWindow.closed) {
      popupWindow.close();
    }
    popupWindowRef.current = null;
  }, []);

  return { openPopup, navigatePopup, closePopup };
}
